import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import {
  IOU_ATTACHMENT_UPLOAD,
  IOU_COST_ADD_ENDPOINT,
  IOU_COST_CREATE_ENDPOINT,
  IOU_COST_EDIT_ENDPOINT,
  IOU_COST_STORE_ENDPOINT,
  IOU_PURPOSE_LIST_ENDPOINT,
  IOU_WISE_DOCUMENT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import UploadDocument from "./UploadDocument";
import IOUDocumentUpload from "../IOUCost/IOUDocumentUpload";

const { TextArea } = Input;
const { Option } = Select;

/**
 * Main Component
 */
const IOUDocumentsForm = forwardRef((props, ref) => {
  const [userType, setUserType] = useState("");
  const [purposeList, setPurposeList] = useState([]);
  const [purposeName, setPurposeName] = useState("");
  const { form, isEdit, editInfo, viewMode, getIOUCostInfo } = props;
  const [filesList, setFileList] = useState([]);
  // Invoice-wise expenditure state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [invoiceExpNumbers, setInvoiceExpNumbers] = useState({});
  const [receiptableRows, setReceiptableRows] = useState({}); // { [invoiceId]: rows[] }
  const [nonReceiptableRows, setNonReceiptableRows] = useState({}); // { [invoiceId]: rows[] }
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState({
    salesContracts: false,
    invoices: false,
  });

  const receiptableTypes = [
    "C& F Association Fee",
    "Customs Automation Fee",
    "DF VAT+Duty (Customs)",
    "Port Charge",
    "Port Demurrage",
    "Shipping NOC Charge",
    "Shipping Demurrage",
    "DOCUMENT Made",
    "Loading Charge",
    "Local Truck Fare",
    "Documents P.Cop/verify & Other",
    "Shortinh Charge",
    "Special Permission",
    "Amendment Fee With Misc.",
  ];

  const nonReceiptableTypes = [
    "Bill of Entry Noting TO Assment",
    "Examination Up To Dly(import) / Goods Unstaffing Purpose(export)",
    "RAD SAVE",
    "100% Examination Report Purpose AIR Jetty",
    "100% Examination Report Purpose RO+ARO(Jetty)",
    "100% Examination Red Cancel Purpose DC+Peon Customs",
    "100% Examination Assment  RO+ARO(Customs)",
    "HS CODE PURPOSE/Regarding Fabric Cutting & Special Pemission",
  ];

  const navigate = useNavigate();

  useEffect(() => {
    const username = localStorage.getItem("name") || "";

    const roleInfoStr = localStorage.getItem("roleInfo") || "{}";
    let roleInfo;
    try {
      roleInfo = JSON.parse(roleInfoStr);
    } catch (e) {
      roleInfo = {};
    }

    const userRole = roleInfo.name || "";
    setUserType(userRole);

    if (!isEdit) {
      const defaultValues = {
        iou_date: moment(),
        taken_by: username,
        nature_of_payments: "NRA",
        requested_on: moment(),
      };

      if (userRole === "Head of Commercial & Shipping") {
        defaultValues.iou_for = "CGL";
        defaultValues.nature_of_payments = "RA";
      } else if (userRole === "Director") {
        defaultValues.iou_for = "OTL";
      }

      form.setFieldsValue(defaultValues);
    }
  }, [isEdit, form, props.currentUser]);

  useEffect(() => {
    if (editInfo) {
      form.setFieldsValue({
        iou_for: editInfo?.iou_for,
        iou_number: editInfo?.iou_number,
        ra_amount: editInfo?.ra_amount,
        nra_amount: editInfo?.nra_amount,
        iou_amount: editInfo?.iou_amount,
        purpose_id: editInfo?.purpose_id,
        comments: editInfo?.comments,
        taken_by: editInfo?.user?.name || "",
        receive_by: editInfo?.user?.name || editInfo?.taken_by || "",
        iou_date: editInfo?.iou_date ? moment(editInfo?.iou_date) : null,
        approval_status: editInfo?.approval_status || "pending",
      });

      const responseDocument = editInfo?.attachments?.map((item) => {
        return {
          id: item?.id,
          name: item?.attachment_name,
          document: item?.attachment,
          path: item?.attachment,
        };
      });
      setFileList(responseDocument || []);
    }
  }, [editInfo, form]);

  const getIOUDocuments = useCallback(
    async (iouId) => {
      if (!iouId) {
        setFileList([]);
        return;
      }

      try {
        const query = `${IOU_WISE_DOCUMENT}/${iouId}`;
        const response = await getData(query);

        if (response && response.status === 200) {
          const documents = Array.isArray(response.data?.data) ? response.data.data : [];

          const mappedDocs = documents.map((doc) => ({
            id: doc.id,
            name: doc.document_name,
            path: doc.document,
            status: doc.status,
            comments: doc.comments || "",
          }));

          setFileList(mappedDocs);
        } else {
          setFileList([]);
        }
      } catch (error) {
        setFileList([]);
        notification.error({
          message: "Error",
          description: "Failed to fetch IOU documents",
          placement: "topRight",
        });
      }
    },
    [setFileList],
  );

  useEffect(() => {
    if (editInfo?.id) {
      getIOUDocuments(editInfo.id);
    } else {
      setFileList([]);
    }
  }, [editInfo, getIOUDocuments]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        // Validate receiptable & non-receiptable rows
        if (!validateReceiptableRows() || !validateNonReceiptableRows()) {
          notification.error({
            message: "Validation Error",
            description: "Amount is required and must be greater than 0 when type is selected",
            placement: "topRight",
          });
          return;
        }

        const user_id = localStorage.getItem("user_id") || null;

        const formData = new FormData();
        formData.append("iou_for", values.iou_for);
        formData.append("iou_number", values.iou_number);
        formData.append("iou_amount", values.iou_amount);
        formData.append("ra_amount", values.ra_amount);
        formData.append("nra_amount", values.nra_amount);
        formData.append("purpose_id", values.purpose_id);

        if (values.comments) {
          formData.append("comments", values.comments);
        }

        formData.append("iou_date", values.iou_date.format("YYYY-MM-DD"));

        if (values.approval_status) {
          formData.append("approval_status", values.approval_status);
        }

        if (isEdit) {
          if (userType === "Head of Commercial & Shipping") {
            formData.append("department_head_id", user_id);
            formData.append("department_approve", values.approval_status || "");
          } else if (userType === "Director") {
            formData.append("director_id", user_id);
            formData.append("director_approve", values.approval_status || "");
          }
        }

        const query = isEdit ? `${IOU_COST_EDIT_ENDPOINT}/${editInfo?.id}` : IOU_COST_ADD_ENDPOINT;

        const response = await postData(query, formData);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description: response?.message || `IOU Disbursement ${isEdit ? "updated" : "created"} successfully`,
            placement: "bottomRight",
          });
          navigate("/import-export-expense-tracking/iou-disbursement");
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to submit IOU Disbursement form",
          placement: "topRight",
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, editInfo, navigate, receiptableRows, nonReceiptableRows],
  );

  // const isFieldDisabled = useCallback(
  //   (fieldName) => {
  //     if (viewMode) return true;

  //     if (
  //       isEdit &&
  //       (userType === "Head of Commercial & Shipping" ||
  //         userType === "Director")
  //     ) {
  //       return fieldName !== "iou_amount" && fieldName !== "approval_status";
  //     }

  //     return false;
  //   },
  //   [isEdit, userType, viewMode],
  // );

  const getIOUNumber = useCallback(async (val) => {
    let url = val ? `${IOU_COST_CREATE_ENDPOINT}?type=${val}` : IOU_COST_CREATE_ENDPOINT;
    try {
      const response = await getData(url);
      if (response && response.status === 200) {
        form.setFieldsValue({ iou_number: response.data.data });
      } else {
        notification.error({
          message: "Error",
          description: "Failed to generate IOU number",
          placement: "topRight",
        });
        return "";
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to generate IOU number",
        placement: "topRight",
      });
      return "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({ form }), [form]);

  const getIOUPurposeList = useCallback(async () => {
    try {
      const bodyData = {
        per_page: 1000,
      };
      const response = await getData(IOU_PURPOSE_LIST_ENDPOINT, false, bodyData);
      if (response && response.status === 200) {
        setPurposeList(response?.data?.data?.data);
        return response.data.data;
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch IOU Purpose List",
        placement: "topRight",
      });
      return [];
    }
  }, []);

  // Get invoice numbers for filter
  const getInvoiceNumbers = async () => {
    setLoading((prev) => ({ ...prev, invoices: true }));
    const response = await getData(`/api/accounting/export_invoice_numbers`);
    if (response?.status === 200) {
      setInvoices(response?.data?.data || []);
    }
    setLoading((prev) => ({ ...prev, invoices: false }));
  };

  // Handle invoice selection and auto-populate fields
  const handleInvoiceChange = (invoiceId) => {
    if (!invoiceId) {
      // Clear fields if invoice is cleared
      form.setFieldsValue({
        invoice_value: undefined,
        invoice_quantity: undefined,
        invoice_quantity_unit: undefined,
        sales_contract_number: undefined,
        exp_number: undefined,
        exp_date: undefined,
      });
      return;
    }

    const selectedInvoice = invoices.find((inv) => inv.id === invoiceId);
    if (selectedInvoice) {
      form.setFieldsValue({
        invoice_value: selectedInvoice.invoice_value || undefined,
        invoice_quantity: selectedInvoice.total_quantity || undefined,
        invoice_quantity_unit: undefined, // Not in response, keep as is
        sales_contract_number: selectedInvoice.sales_contract_info?.reference_no || undefined,
        exp_number: selectedInvoice.exp_number || undefined,
        exp_date: selectedInvoice.exp_date ? moment(selectedInvoice.exp_date) : undefined,
      });
    }
  };

  useEffect(() => {
    getInvoiceNumbers();
  }, []);

  useEffect(() => {
    getIOUPurposeList();
  }, [getIOUPurposeList]);

  const addPurpose = useCallback(
    async (purposeName) => {
      try {
        const response = await postData(IOU_COST_STORE_ENDPOINT, {
          name: purposeName,
        });

        if (response && response.code === 200) {
          getIOUPurposeList();
        } else {
          notification.error({
            message: "Error",
            description: "Failed to add purpose",
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to add purpose",
          placement: "topRight",
        });
      }
    },
    [getIOUPurposeList],
  );

  const addReceiptableRow = (invoiceId) => {
    const newRow = {
      id: Date.now(),
      type: undefined,
      amount: undefined,
    };
    setReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: [...(prev[invoiceId] || []), newRow],
    }));
  };

  const addNonReceiptableRow = (invoiceId) => {
    const newRow = {
      id: Date.now(),
      type: undefined,
      amount: undefined,
    };
    setNonReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: [...(prev[invoiceId] || []), newRow],
    }));
  };

  const updateReceiptableRow = (invoiceId, id, field, value) => {
    setReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));
  };

  const deleteReceiptableRow = (invoiceId, id) => {
    setReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).filter((row) => row.id !== id),
    }));
  };

  const updateNonReceiptableRow = (invoiceId, id, field, value) => {
    setNonReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));
  };

  const deleteNonReceiptableRow = (invoiceId, id) => {
    setNonReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).filter((row) => row.id !== id),
    }));
  };

  const getSelectedTypes = (invoiceId) => {
    return (receiptableRows[invoiceId] || [])
      .map((row) => row.type)
      .filter((type) => type !== undefined && type !== null);
  };

  const getSelectedNonReceiptableTypes = (invoiceId) => {
    return (nonReceiptableRows[invoiceId] || [])
      .map((row) => row.type)
      .filter((type) => type !== undefined && type !== null);
  };

  const getTotalReceiptable = (invoiceId) => {
    return (receiptableRows[invoiceId] || []).reduce((sum, row) => {
      const amount = parseFloat(row.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const getTotalNonReceiptable = (invoiceId) => {
    return (nonReceiptableRows[invoiceId] || []).reduce((sum, row) => {
      const amount = parseFloat(row.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const validateReceiptableRows = () => {
    for (const invoiceId of selectedInvoiceIds) {
      const rows = receiptableRows[invoiceId] || [];
      for (const row of rows) {
        if (row.type) {
          if (!row.amount || parseFloat(row.amount) <= 0) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const getReceiptableRowError = (row) => {
    if (row.type && (!row.amount || parseFloat(row.amount) <= 0)) {
      return "Amount is required and must be greater than 0";
    }
    return null;
  };

  const validateNonReceiptableRows = () => {
    for (const invoiceId of selectedInvoiceIds) {
      const rows = nonReceiptableRows[invoiceId] || [];
      for (const row of rows) {
        if (row.type) {
          if (!row.amount || parseFloat(row.amount) <= 0) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const getNonReceiptableRowError = (row) => {
    if (row.type && (!row.amount || parseFloat(row.amount) <= 0)) {
      return "Amount is required and must be greater than 0";
    }
    return null;
  };

  const getReceiptableColumns = (invoiceId) => [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "60%",
      render: (text, record) => {
        const selectedTypes = getSelectedTypes(invoiceId);
        return (
          <Select
            placeholder="Select type"
            value={record.type}
            onChange={(value) => updateReceiptableRow(invoiceId, record.id, "type", value)}
            style={{ width: "100%" }}
            allowClear
          >
            {receiptableTypes.map((type) => (
              <Option key={type} value={type} disabled={selectedTypes.includes(type) && record.type !== type}>
                {type}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "30%",
      render: (text, record) => {
        const error = getReceiptableRowError(record);
        return (
          <div>
            <InputNumber
              placeholder="Enter amount"
              value={record.amount}
              onChange={(value) => updateReceiptableRow(invoiceId, record.id, "amount", value)}
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              precision={2}
              status={error ? "error" : ""}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
            {error && <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (text, record) => {
        return (
          <Popconfirm
            title="Delete Row"
            description="Are you sure you want to delete this row?"
            onConfirm={() => deleteReceiptableRow(invoiceId, record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const getNonReceiptableColumns = (invoiceId) => [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "60%",
      render: (text, record) => {
        const selectedTypes = getSelectedNonReceiptableTypes(invoiceId);
        return (
          <Select
            placeholder="Select type"
            value={record.type}
            onChange={(value) => updateNonReceiptableRow(invoiceId, record.id, "type", value)}
            style={{ width: "100%" }}
            allowClear
          >
            {nonReceiptableTypes.map((type) => (
              <Option key={type} value={type} disabled={selectedTypes.includes(type) && record.type !== type}>
                {type}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "30%",
      render: (text, record) => {
        const error = getNonReceiptableRowError(record);
        return (
          <div>
            <InputNumber
              placeholder="Enter amount"
              value={record.amount}
              onChange={(value) => updateNonReceiptableRow(invoiceId, record.id, "amount", value)}
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              precision={2}
              status={error ? "error" : ""}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
            {error && <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (text, record) => {
        return (
          <Popconfirm
            title="Delete Row"
            description="Are you sure you want to delete this row?"
            onConfirm={() => deleteNonReceiptableRow(invoiceId, record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const handleInvoiceMultiSelectChange = (ids) => {
    setSelectedInvoiceIds(ids || []);
  };

  return (
    <div className="containt-body">
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={true}
          initialValues={{
            ra_amount: 0,
            nra_amount: 0,
            iou_amount: 0,
            requested_on: moment(),
          }}
          onValuesChange={(changedValues, allValues) => {
            if ("ra_amount" in changedValues || "nra_amount" in changedValues) {
              const raAmount = allValues.ra_amount || 0;
              const nraAmount = allValues.nra_amount || 0;
              form.setFieldsValue({ iou_amount: raAmount + nraAmount });
            }
          }}
        >
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item
                label="IOU For"
                name="iou_for"
                rules={[
                  {
                    required: true,
                    message: "Please select IOU For",
                  },
                ]}
              >
                <Select
                  placeholder="Select department"
                  disabled={viewMode}
                  options={[
                    { label: "CGL", value: "CGL" },
                    { label: "OTL", value: "OTL" },
                    { label: "OIL", value: "OIL" },
                  ]}
                  onChange={getIOUNumber}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="IOU Number" name="iou_number">
                <Input placeholder="IOU Number" disabled={true} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="RA"
                name="ra_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="NRA"
                name="nra_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Amount"
                name="iou_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {/* <Col span={4}>
              <Form.Item label="Register Entry No" name="register_entry_no">
                <Input
                  placeholder="Enter register entry number"
                  disabled={isFieldDisabled("register_entry_no")}
                />
              </Form.Item>
            </Col> */}
            {/* <Col span={4}>
              <Form.Item
                label="Nature Of Payments"
                name="nature_of_payments"
                rules={[
                  {
                    required: true,
                    message: "Please select nature of payments",
                  },
                ]}
              >
                <Radio.Group disabled={isFieldDisabled("nature_of_payments")}>
                  <Radio value="RA">RA</Radio>
                  <Radio value="NRA">NRA</Radio>
                </Radio.Group>
              </Form.Item>
            </Col> */}

            <Col span={4}>
              <Form.Item
                label="Requested By"
                name="receive_by"
                rules={[{ required: true, message: "Please enter Requested By" }]}
              >
                <Input
                  placeholder="Auto filled with logged-in user"
                  // disabled={true}
                  disabled={viewMode}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="Date" name="iou_date">
                <DatePicker style={{ width: "100%" }} disabled={true} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              <Form.Item
                label="Purpose"
                name="purpose"
                rules={[
                  {
                    required: true,
                    message: "Please enter purpose",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter purpose"
                  disabled={isFieldDisabled("purpose")}
                />
              </Form.Item>
            </Col> */}
            <Col span={4}>
              <Form.Item
                label="Purpose"
                name="purpose_id"
                rules={[
                  {
                    required: true,
                    message: "Purpose is required",
                  },
                ]}
              >
                <Input.Group compact>
                  <Form.Item
                    name="purpose_id"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <Select
                      name="purpose_id"
                      required
                      showArrow
                      style={{
                        width: "100%",
                      }}
                      placeholder="Name"
                      size="small"
                      allowClear
                      disabled={viewMode}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider
                            style={{
                              margin: "8px 0",
                            }}
                          />
                          <Space
                            style={{
                              padding: "0 8px 4px",
                            }}
                          >
                            <Input
                              placeholder="Purpose"
                              value={purposeName}
                              onChange={(e) => setPurposeName(e.target.value)}
                              size="small"
                            />

                            <Button
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                if (purposeName) {
                                  addPurpose(purposeName);
                                  setPurposeName("");
                                } else {
                                  notification.warning({
                                    message: "Warning",
                                    description: "Please enter a purpose name",
                                    placement: "bottomRight",
                                  });
                                }
                              }}
                            >
                              Add
                            </Button>
                          </Space>
                        </>
                      )}
                    >
                      {purposeList?.map((purpose, idx) => (
                        <Option value={purpose.id} key={idx} id={purpose?.id} label={purpose.name}>
                          {purpose.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Requested On" name="requested_on">
                <DatePicker disabled style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Comments" name="comments">
                <TextArea rows={5} style={{ resize: "none" }} placeholder="Comments" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Card
                style={{
                  marginTop: 16,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                }}
                headStyle={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  borderRadius: "8px 8px 0 0",
                  padding: "16px 24px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
                title="Invoice-wise Expenditure Details"
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="Invoice Number(s)"
                      name="invoice_ids"
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one Invoice Number",
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        size="small"
                        placeholder="Select Invoice Number(s)"
                        loading={loading.invoices}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        onChange={handleInvoiceMultiSelectChange}
                        value={selectedInvoiceIds}
                      >
                        {invoices.map((invoice) => (
                          <Option key={invoice.id} value={invoice.id}>
                            {invoice.invoice_number}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {selectedInvoiceIds.map((invoiceId) => {
                  const invoice = invoices.find((inv) => inv.id === invoiceId) || {};
                  const invoiceReceiptableRows = receiptableRows[invoiceId] || [];
                  const invoiceNonReceiptableRows = nonReceiptableRows[invoiceId] || [];

                  return (
                    <Card
                      key={invoiceId}
                      style={{ marginTop: 16, borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
                      type="inner"
                      title={
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 600 }}>Invoice: {invoice.invoice_number || "N/A"}</span>
                          <div style={{ marginTop: 8, fontSize: 12 }}>
                            <Row gutter={12} align="middle">
                              <Col span={6}>
                                <div>
                                  <strong>Invoice Value</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={
                                    invoice.invoice_value != null
                                      ? Number(invoice.invoice_value)
                                          .toFixed(2)
                                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                      : ""
                                  }
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Invoice Quantity</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={invoice.total_quantity != null ? invoice.total_quantity : ""}
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Sales Contract No</strong>
                                </div>
                                <Input size="small" disabled value={invoice.sales_contract_info?.reference_no || ""} />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Imp/Exp No</strong>
                                </div>
                                <Input
                                  size="small"
                                  placeholder="Enter Imp/Exp No."
                                  onChange={(e) =>
                                    setInvoiceExpNumbers((prev) => ({
                                      ...prev,
                                      [invoiceId]: e.target.value,
                                    }))
                                  }
                                />
                              </Col>
                            </Row>
                          </div>
                        </div>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Card
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <span>Receiptable Details (Expenditure)</span>
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  onClick={() => addReceiptableRow(invoiceId)}
                                  size="middle"
                                  style={{
                                    borderRadius: "6px",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    background: "#fff",
                                    color: "#667eea",
                                    borderColor: "#fff",
                                  }}
                                >
                                  Add Row
                                </Button>
                              </div>
                            }
                            size="small"
                            headStyle={{
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "#fff",
                              borderRadius: "8px 8px 0 0",
                              padding: "8px 16px",
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                            bodyStyle={{
                              padding: "16px",
                            }}
                          >
                            <Form.Item
                              rules={[
                                {
                                  validator: () => {
                                    if (!validateReceiptableRows()) {
                                      return Promise.reject(
                                        new Error(
                                          "Amount is required and must be greater than 0 when type is selected",
                                        ),
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Table
                                columns={getReceiptableColumns(invoiceId)}
                                dataSource={invoiceReceiptableRows}
                                rowKey="id"
                                pagination={false}
                                bordered
                                style={{
                                  borderRadius: "6px",
                                  overflow: "hidden",
                                }}
                                summary={() => {
                                  const total = getTotalReceiptable(invoiceId);
                                  return (
                                    <Table.Summary fixed>
                                      <Table.Summary.Row
                                        style={{
                                          backgroundColor: "#f5f5f5",
                                        }}
                                      >
                                        <Table.Summary.Cell index={0} colSpan={1}>
                                          <strong style={{ fontSize: "14px", color: "#1890ff" }}>
                                            Total Receiptable (Expenditure)
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                          <strong style={{ fontSize: "14px", color: "#1890ff" }}>
                                            {total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} />
                                      </Table.Summary.Row>
                                    </Table.Summary>
                                  );
                                }}
                              />
                            </Form.Item>
                          </Card>
                        </Col>

                        <Col span={12}>
                          <Card
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <span>Non Receiptable Amount (Expenditure)</span>
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  onClick={() => addNonReceiptableRow(invoiceId)}
                                  size="middle"
                                  style={{
                                    borderRadius: "6px",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    background: "#fff",
                                    color: "#667eea",
                                    borderColor: "#fff",
                                  }}
                                >
                                  Add Row
                                </Button>
                              </div>
                            }
                            size="small"
                            headStyle={{
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "#fff",
                              borderRadius: "8px 8px 0 0",
                              padding: "8px 16px",
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                            bodyStyle={{
                              padding: "16px",
                            }}
                          >
                            <Form.Item
                              rules={[
                                {
                                  validator: () => {
                                    if (!validateNonReceiptableRows()) {
                                      return Promise.reject(
                                        new Error(
                                          "Amount is required and must be greater than 0 when type is selected",
                                        ),
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Table
                                columns={getNonReceiptableColumns(invoiceId)}
                                dataSource={invoiceNonReceiptableRows}
                                rowKey="id"
                                pagination={false}
                                bordered
                                style={{
                                  borderRadius: "6px",
                                  overflow: "hidden",
                                }}
                                summary={() => {
                                  const total = getTotalNonReceiptable(invoiceId);
                                  return (
                                    <Table.Summary fixed>
                                      <Table.Summary.Row
                                        style={{
                                          backgroundColor: "#f5f5f5",
                                        }}
                                      >
                                        <Table.Summary.Cell index={0} colSpan={1}>
                                          <strong style={{ fontSize: "14px", color: "#1890ff" }}>
                                            Total Non Receiptable (Expenditure)
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                          <strong style={{ fontSize: "14px", color: "#1890ff" }}>
                                            {total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} />
                                      </Table.Summary.Row>
                                    </Table.Summary>
                                  );
                                }}
                              />
                            </Form.Item>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </Card>
            </Col>

            <Col span={24}>
              <IOUDocumentUpload
                DOC_ADD={IOU_ATTACHMENT_UPLOAD}
                setFileList={setFileList}
                filesList={filesList}
                view={viewMode}
                // onFileDelete={handleFileDelete}
                userId={null}
              />
            </Col>

            {/* <Col span={24}>
              {(userType === "Head of Commercial & Shipping" ||
                userType === "Director") && (
                <Form.Item
                  label="Approval Status"
                  name="approval_status"
                  rules={[
                    {
                      required: true,
                      message: "Please select approval status",
                    },
                  ]}
                >
                  <Radio.Group disabled={isFieldDisabled("approval_status")}>
                    <Radio value="approved">Approved</Radio>
                    <Radio value="pending">Pending</Radio>
                    <Radio value="rejected">Rejected</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            </Col> */}
          </Row>
        </Form>
      </Card>
      <div className="my-2">
        <UploadDocument
          record={editInfo}
          setFileList={setFileList}
          filesList={filesList}
          getIOUCostInfo={getIOUCostInfo}
        />
      </div>
    </div>
  );
});

IOUDocumentsForm.displayName = "IOUCostForm";

export default IOUDocumentsForm;
